import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

type ClientLite = {
  id: string;
  name: string | null;
  website_url: string | null;
};

type AnchorMap = Record<string, { en?: string[]; fr?: string[] }>;

export default function CleanAnchorsPanel() {
  const [clients, setClients] = useState<ClientLite[]>([]);
  const [clientId, setClientId] = useState<string>("");
  const [serviceUrls, setServiceUrls] = useState<Record<string, string>>({});
  const [anchors, setAnchors] = useState<AnchorMap>({});
  const [languages, setLanguages] = useState<("en" | "fr")[]>(["en"]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const { data, error } = await supabase.functions.invoke("admin-list-clients");
      if (error) {
        toast({ title: "Failed to load clients", description: error.message, variant: "destructive" });
        return;
      }
      const list: ClientLite[] = (data?.clients ?? []).map((c: any) => ({
        id: c.id, name: c.name, website_url: c.website_url,
      }));
      list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setClients(list);
      if (list.length && !clientId) setClientId(list[0].id);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!clientId) return;
    (async () => {
      setLoading(true);
      const { data, error } = await supabase.functions.invoke("admin-get-client-anchors", {
        body: { client_id: clientId },
      });
      setLoading(false);
      if (error || (data as any)?.error) {
        toast({
          title: "Failed to load client",
          description: error?.message || (data as any)?.error || "Unknown error",
          variant: "destructive",
        });
        return;
      }
      setServiceUrls(((data as any)?.service_urls as any) || {});
      setAnchors(((data as any)?.clean_anchors as AnchorMap) || {});
      const langs = (data as any)?.languages as ("en" | "fr")[] | undefined;
      setLanguages(Array.isArray(langs) && langs.length ? langs : ["en"]);
    })();
  }, [clientId]);

  const urlEntries = useMemo(
    () => Object.entries(serviceUrls).filter(([k, v]) => !!v && k !== "homepage"),
    [serviceUrls],
  );

  const updateAnchors = (url: string, lang: "en" | "fr", value: string) => {
    const list = value.split("\n").map((s) => s.trim()).filter(Boolean);
    setAnchors((prev) => ({
      ...prev,
      [url]: { ...(prev[url] || {}), [lang]: list },
    }));
  };

  const save = async () => {
    if (!clientId) return;
    setSaving(true);
    const { data, error } = await supabase.functions.invoke("admin-update-clean-anchors", {
      body: { client_id: clientId, clean_anchors: anchors },
    });
    setSaving(false);
    if (error || (data as any)?.error) {
      toast({
        title: "Save failed",
        description: error?.message || (data as any)?.error || "Unknown error",
        variant: "destructive",
      });
      return;
    }
    toast({ title: "Saved", description: "clean_anchors updated for this client." });
  };

  return (
    <div className="mb-8 rounded-[14px] border border-border bg-card p-5">
      <div className="mb-4">
        <h2 className="text-lg font-semibold">Clean anchors</h2>
        <p className="text-xs text-muted-foreground mt-1">
          Per service URL: list the exact anchor phrases the linker may use. One phrase per line.
        </p>
      </div>

      <div className="mb-4 flex items-center gap-3">
        <select
          value={clientId}
          onChange={(e) => setClientId(e.target.value)}
          className="h-10 rounded-[10px] border border-border bg-background px-3 text-sm flex-1"
        >
          {clients.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name || c.id.slice(0, 8)} {c.website_url ? `— ${c.website_url}` : ""}
            </option>
          ))}
        </select>
        <button
          onClick={save}
          disabled={saving || loading}
          className="h-10 rounded-[10px] bg-primary px-5 text-sm font-semibold text-primary-foreground disabled:opacity-60"
        >
          {saving ? "Saving…" : "Save anchors"}
        </button>
      </div>

      {loading && <div className="text-sm text-muted-foreground">Loading…</div>}

      {!loading && urlEntries.length === 0 && (
        <div className="text-sm text-muted-foreground">
          No service URLs configured for this client.
        </div>
      )}

      {!loading && urlEntries.length > 0 && (
        <div className="space-y-4">
          {urlEntries.map(([key, url]) => (
            <div key={key} className="rounded-[10px] border border-border p-4">
              <div className="mb-3">
                <div className="text-sm font-semibold">{key}</div>
                <div className="text-xs text-muted-foreground break-all">{url}</div>
              </div>
              <div className={`grid grid-cols-1 ${languages.length > 1 ? "md:grid-cols-2" : ""} gap-3`}>
                {languages.map((lang) => {
                  const list = anchors[url]?.[lang] || [];
                  return (
                    <div key={lang}>
                      <div className="mb-1">
                        <label className="text-xs font-semibold uppercase">{lang}</label>
                      </div>
                      <textarea
                        value={list.join("\n")}
                        onChange={(e) => updateAnchors(url, lang, e.target.value)}
                        rows={5}
                        placeholder={lang === "fr" ? "ex. serrurier d'urgence" : "e.g. commercial locksmith"}
                        className="w-full rounded-[8px] border border-border bg-background p-2 text-xs font-mono"
                      />
                      <div className="mt-1 text-[11px] text-muted-foreground">
                        {list.length} phrase{list.length === 1 ? "" : "s"}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
